import React, { ReactNode, useContext, useEffect, useState } from "react";
import { TooltipContext, TooltipProviderValue } from "./TooltipProvider";
import { clamp } from "./Functions";

export interface SpeedometerSegment {
  endValue: number,
  colorStyle: string,
}

interface Props {
  startValue?: number,
  isLoading?: boolean,
  value: number,
  description?: string,
  segments?: SpeedometerSegment[],
  formatValue?: (value: number) => string, // defaults to formatValuePercentage
  getTooltip?: (segmentFrom: SpeedometerSegment, segmentTo: SpeedometerSegment) => string, // defaults to getTooltipPercentage
}

const formatValuePercentage = (value: number) => (value === null || value === undefined) ? "--" : (Math.round((value ?? 0) * 100) + "%");

const getTooltipPercentage = (segmentFrom: SpeedometerSegment, segmentTo: SpeedometerSegment, formatValue: (value: number) => string) =>
  `${formatValue(segmentFrom?.endValue ?? 0)} - ${formatValue(segmentTo.endValue)}`;

export const getDefaultColor4Segment = (endValue1: number, endValue2: number, endValue3: number, endValue4: number): SpeedometerSegment[] => [
  { colorStyle: "#EB0004", endValue: endValue1 },
  { colorStyle: "#E6C700", endValue: endValue2 },
  { colorStyle: "#70A800", endValue: endValue3 },
  { colorStyle: "#005C00", endValue: endValue4 },
];

const Speedometer = ({
  startValue = 0,
  segments,
  isLoading,
  value,
  formatValue = formatValuePercentage,
  description,
  getTooltip,
}: Props) => {
  const [needlePath, setNeedlePath] = useState<string>();
  const [segmentData, setSegmentData] = useState<{
    d: string,
    color: string
    tooltip: string,
    value: number,
  }[]>();
  const tooltip = useContext<TooltipProviderValue>(TooltipContext);
  
  const size = 300;
  const strokeWidth = 45;
  const needleWidth = 15;

  const radius = size / 2;
  const cx = radius;
  const cy = radius;

  // map [0,1] to angles left to right [180 deg - 360 deg]
  const toAngle = (f: number) => Math.PI + f * Math.PI;

  const polar = (angle: number, r: number) => ({
    x: cx + Math.cos(angle) * r,
    y: cy + Math.sin(angle) * r,
  });

  const buildArc = (start: number, end: number, r: number) => {
    const a1 = polar(start, r);
    const a2 = polar(end,   r);
    
    const largeArc = Math.abs(end - start) > Math.PI ? 1 : 0;
    const sweepFlag = 1;
    return `M ${a1.x} ${a1.y} A ${r} ${r} 0 ${largeArc} ${sweepFlag} ${a2.x} ${a2.y}`;
  };
  
  useEffect(() => {
    const sortedSegments = [...segments].sort((a, b) => a.endValue - b.endValue);

    const totalMax = sortedSegments[sortedSegments.length - 1].endValue;

    const segmentsAdjusted = sortedSegments.map(s => ({
      endPercentage: (s.endValue - startValue) / (totalMax - startValue),
      segment: s,
    }));

    // generate all the segment paths
    let prevPercentage = 0;
    let prevSegment: SpeedometerSegment;

    const tooltipFunc = getTooltip ?? ((from, to) => getTooltipPercentage(from, to, formatValue));

    setSegmentData(segmentsAdjusted.map(({ endPercentage, segment }, i) => {
      let sAng = toAngle(prevPercentage);
      let eAng = toAngle(endPercentage);
      prevPercentage = endPercentage;
      let tooltip = tooltipFunc(prevSegment, segment);
      prevSegment = segment;

      return {
        d: buildArc(sAng, eAng, radius - strokeWidth / 2),
        color: segment.colorStyle,
        tooltip: tooltip,
        value: segment.endValue,
      };
    }));
  }, [segments]);


  useEffect(() => {
    if (!segmentData) return;
    
    let maxValue = segmentData[segmentData.length - 1].value;
    let clampedSpeedometerValue = clamp(value, startValue, maxValue);
    
    // triangle needle
    let valFrac = (clampedSpeedometerValue - startValue) / (maxValue - startValue);
    let nAng = toAngle(valFrac);
    let nLen = radius - strokeWidth / 2;
    let nW = needleWidth;
    let tip = polar(nAng, nLen);
    let left = polar(nAng + Math.PI / 2, nW / 2);
    let right = polar(nAng - Math.PI / 2, nW / 2);
    setNeedlePath(`M ${left.x} ${left.y} L ${tip.x} ${tip.y} L ${right.x} ${right.y} Z`);
  }, [value, segmentData]);

  let speedoNode: ReactNode = null;

  if (needlePath && segmentData) {
    speedoNode = (
      <>
        <svg viewBox={`0 0 ${size} ${(size + needleWidth) / 2}`}>
          {/* colored arcs */}
          {segmentData.map((seg, i) => (
            <path
              key={i}
              d={seg.d}
              stroke={seg.color}
              strokeWidth={strokeWidth}
              fill="none"
              onMouseOver={seg.tooltip && (() => tooltip.show(seg.tooltip))}
              onMouseOut={seg.tooltip && tooltip.hide}
            />
          ))}

          {/* needle and center */}
          {(typeof value === "number" && !isLoading) && <>
            <path d={needlePath} fill="var(--speedometer-needle-color)" />
            <circle cx={cx} cy={cy} r={needleWidth * 0.5} fill="var(--speedometer-needle-color)" />
          </>}
        </svg>
        <div className="speedometer-value">{isLoading ? "Loading..." : formatValue(value)}</div>
        {description && <div className="speedometer-description">{description}</div>}
      </>
    );
  }

  return <div className="speedometer-control">{speedoNode}</div>;
};

export default Speedometer;
